import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useFetchDeclarant } from 'modules/Declarant/hooks/useFetchDeclarant';
import { isUkRegulation } from 'modules/Common/utils/regulation';
import { useOrgType } from 'modules/Common/hooks/useOrgType';
import { OrganizationType } from 'modules/Common/utils/Utility';

// Regime gate (CG-2312, docs/features/uk-cbam-imports/00-OVERVIEW.md D1).
// Every declarant carries a `regulation` ('EU' | 'UK', backend default 'EU');
// `isUkRegulation` is the one shared reading of it. Redirect targets are each
// other's route (not /not-found): a declarant on the wrong regime's URL almost
// certainly followed a stale link, and each regime has a real Imports page to
// send them to instead.
export const UkRegulationGuard = ({ children }) => {
  const { data: declarant, isLoading } = useFetchDeclarant();
  if (isLoading) return null;
  if (!isUkRegulation(declarant)) return <Redirect to="/imports" />;
  return children;
};

// Only a declarant HAS a regulation, so only a declarant can be on the wrong
// one. Operators and consultants pass straight through without waiting on — or
// firing — a fetch that is not theirs: `useFetchDeclarant` says as much in its
// own comment, and /cost-radar is the first route to put this guard in front of
// those org types. Without the gate an operator watches a blank page for the
// whole retry-and-backoff window, every five minutes, for an answer that could
// never have applied to them.
//
// The redirect keeps the query string: "Used in" links on supplier and
// installation pages point at `/imports?supplierName=…` /
// `?installationName=…`, and the UK page reuses the same URL-synced
// ImportTable, so dropping `location.search` here is what turned those links
// into an unfiltered list for UK declarants.
export const EuRegulationGuard = ({ children, ukRedirect = '/uk/imports' }) => {
  const orgType = useOrgType();
  const location = useLocation();
  const isDeclarant = orgType === OrganizationType.Declarant;
  const { data: declarant, isLoading } = useFetchDeclarant({ enabled: isDeclarant });
  if (!isDeclarant) return children;
  if (isLoading) return null;
  if (isUkRegulation(declarant)) {
    return <Redirect to={{ pathname: ukRedirect, search: location.search }} />;
  }
  return children;
};
